import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { notify } from 'app/Services/app/notificationService';
import { sentry } from 'app/utils/sentry';

/**
 * Error Boundary for Stripe Elements components only.
 * 
 * Wrap directly around <Elements> components to:
 * - Isolate Stripe errors from other component errors
 * - Show Stripe-specific error UI without affecting parent components
 * - Prevent Stripe failures from crashing the entire page
 */
class StripeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log to Sentry
    sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        component: 'StripeErrorBoundary',
      },
    });

    // Show user-friendly notification
    if (error.message?.includes('Stripe')) {
      notify.error(
        'Payment system unavailable. Please check your ad blocker or try refreshing the page.',
      );
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: '32px',
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            margin: '16px 0',
          }}
        >
          <h3 style={{ color: '#856404', marginBottom: '16px' }}>
            Payment System Unavailable
          </h3>
          <p style={{ color: '#856404', marginBottom: '16px' }}>
            We're having trouble loading the payment system. This might be due to:
          </p>
          <ul
            style={{
              color: '#856404',
              textAlign: 'left',
              display: 'inline-block',
              marginBottom: '16px',
            }}
          >
            <li>Ad blockers or privacy extensions blocking payment scripts</li>
            <li>Network connectivity issues</li>
            <li>Corporate firewall restrictions</li>
          </ul>
          <p style={{ color: '#856404', marginBottom: '16px' }}>
            Please try the following:
          </p>
          <ul
            style={{
              color: '#856404',
              textAlign: 'left',
              display: 'inline-block',
              marginBottom: '16px',
            }}
          >
            <li>Disable ad blockers for this site</li>
            <li>Refresh the page</li>
            <li>Try a different browser</li>
            <li>Check your internet connection</li>
          </ul>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

StripeErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default StripeErrorBoundary;
